import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGetComparisonFormLocal } from 'pages/ProductComparison/hooks/useGetComparisonFormLocal'
import { useAppDispatch } from '@/store/hook'
import { trackProductsComparison } from '@/store/slices/adobeAnalyticSlice'
import { PRODUCT_COMPARISON } from '@/libs/constants/productTypes'

const MAX_COMPARISON_ITEMS = 3

/**
 * Parse comparison products from localStorage
 * @returns {Array} Array of comparison products or empty array
 */
const getComparisonProducts = () => {
  try {
    const stored = localStorage.getItem(PRODUCT_COMPARISON)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Failed to parse comparison products:', error)
    return []
  }
}

const useProductComparison = () => {
  const dispatch = useAppDispatch()
  const { listProductIds, deleteProductIds } = useGetComparisonFormLocal()
  const isMountedRef = useRef(true)

  const [showComparisonModal, setShowComparisonModal] = useState(false)
  const [comparingProductIds, setComparingProductIds] = useState(listProductIds)
  const [productInformation, setProductInformation] = useState({
    materialNumber: '',
    materialName: '',
    categoryName: '',
    listPrice: '',
    currencyUnit: '',
    image: '',
    displayListPrice: true,
  })

  const getProductData = useCallback(() => {
    const compareProducts = getComparisonProducts()
    const comparisonAnalysisList = []

    const productIds = compareProducts.map(({ materialNumber, materialName }) => {
      comparisonAnalysisList.push(`${materialNumber}:${materialName}`)
      return materialNumber
    })

    return { productIds, comparisonAnalysisList }
  }, [])

  // Update comparison list from localStorage
  const updateComparisonList = useCallback(() => {
    if (!isMountedRef.current) return

    const { productIds, comparisonAnalysisList } = getProductData()
    setComparingProductIds(productIds)
    dispatch(trackProductsComparison(comparisonAnalysisList))
  }, [getProductData, dispatch])

  // Handle compare checkbox toggle
  const onCompareHandler = useCallback(
    (isChecked, product) => {
      if (isChecked) {
        const isAlreadyComparing = comparingProductIds.includes(product?.materialNumber)

        // Only add if not already comparing AND there's room
        if (!isAlreadyComparing && comparingProductIds.length < MAX_COMPARISON_ITEMS) {
          setComparingProductIds((prev) => [...prev, product?.materialNumber])
        }

        setProductInformation({
          materialNumber: product?.materialNumber,
          materialName: product?.materialName,
          categoryName: product?.categoryName,
          listPrice: product?.listPrice,
          currencyUnit: product?.currencyUnit,
          image: product?.pictureUrl || product?.imageUrl,
          displayListPrice: product?.displayListPrice,
        })

        setShowComparisonModal(true)
      } else {
        setComparingProductIds((prev) => prev.filter((id) => id !== product?.materialNumber))

        deleteProductIds(product?.materialNumber)

        const { comparisonAnalysisList } = getProductData()
        dispatch(trackProductsComparison(comparisonAnalysisList))
      }
    },
    [comparingProductIds, deleteProductIds, getProductData, dispatch],
  )

  const toggleComparisonModal = useCallback(
    (show) => {
      setShowComparisonModal(show)
      if (!show) {
        updateComparisonList()
      }
    },
    [updateComparisonList],
  )

  useEffect(() => {
    isMountedRef.current = true

    updateComparisonList()

    const handleStorageChange = () => {
      updateComparisonList()
    }

    globalThis.addEventListener('productComparisonUpdated', handleStorageChange)

    return () => {
      isMountedRef.current = false
      globalThis.removeEventListener('productComparisonUpdated', handleStorageChange)
    }
  }, [updateComparisonList])

  return useMemo(
    () => ({
      showComparisonModal,
      setShowComparisonModal: toggleComparisonModal,
      comparingProductIds,
      onCompareHandler,
      productInformation,
      refreshComparisonList: updateComparisonList,
    }),
    [
      showComparisonModal,
      toggleComparisonModal,
      comparingProductIds,
      onCompareHandler,
      productInformation,
      updateComparisonList,
    ],
  )
}

export default useProductComparison
